import type { CartItem } from '@/types'

import { formatIDR } from '@/lib/format'
import { siteConfig, WHATSAPP_NUMBER_RAW } from '@/constants/site'

// Accepts 0812..., 812..., +62 812-..., 62812... and returns digits in international format.
export const normalizeWhatsAppNumber = (raw: string): string | null => {
    if (!raw || /replace_me/i.test(raw)) return null

    const digits = raw.replace(/\D/g, '')

    if (digits.length < 8) return null
    if (digits.startsWith('0')) return `62${digits.slice(1)}`
    if (digits.startsWith('8') && digits.length <= 12) return `62${digits}`

    return digits
}

export const WHATSAPP_NUMBER = normalizeWhatsAppNumber(WHATSAPP_NUMBER_RAW)

export const buildWhatsAppUrl = (message: string): string => {
    const text = encodeURIComponent(message)

    return WHATSAPP_NUMBER
        ? `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`
        : `https://wa.me/?text=${text}`
}

const describeItem = (item: { name: string, colorLabel: string | null }): string => {
    return item.colorLabel ? `${item.name} (${item.colorLabel})` : item.name
}

export const buildCartMessage = (items: Array<CartItem>, note: string): string => {
    const lines = items.map((item, index) => {
        return `${index + 1}. ${describeItem(item)} x ${item.quantity} = ${formatIDR(item.price * item.quantity)}`
    })

    const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0)
    const trimmedNote = note.trim()

    return [
        `Hello ${siteConfig.name}! I would like to order:`,
        '',
        ...lines,
        '',
        `Total: ${formatIDR(total)}`,
        trimmedNote ? `\nNote: ${trimmedNote}` : '',
        '',
        'Could you please confirm availability and shipping? Thank you!'
    ].join('\n').replace(/\n{3,}/g, '\n\n').trim()
}

export const buildProductMessage = (input: {
    name: string
    slug: string
    price: number
    quantity: number
    colorLabel: string | null
    madeToOrder: boolean
    note?: string
    // Site origin, known only in the browser; empty during the server render.
    origin?: string
}): string => {
    const url = input.origin ? `${input.origin}/products/${input.slug}` : ''
    const note = input.note?.trim()

    return [
        `Hello ${siteConfig.name}! I would like to ${input.madeToOrder ? 'request' : 'order'}:`,
        '',
        `${describeItem({ name: input.name, colorLabel: input.colorLabel })} x ${input.quantity} = ${formatIDR(input.price * input.quantity)}`,
        url,
        note ? `\nNote: ${note}` : '',
        '',
        input.madeToOrder
            ? 'Could we talk about the details for my custom doll? Thank you!'
            : 'Could you please confirm availability and shipping? Thank you!'
    ].join('\n').replace(/\n{3,}/g, '\n\n').trim()
}

export const buildCustomRequestMessage = (): string => {
    return [
        `Hello ${siteConfig.name}! I would love a custom crochet doll.`,
        '',
        'Who or what it should look like:',
        'Preferred colours:',
        'Size:',
        'When I need it by:',
        '',
        'I can send reference photos here. Thank you!'
    ].join('\n')
}

export const buildQuestionMessage = (): string => {
    return `Hello ${siteConfig.name}! I have a question about your handmade dolls.`
}
